import { Injectable, computed, signal } from '@angular/core';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export type PwaInstallMode = 'native' | 'ios' | 'unsupported';

const DISMISSED_AT_KEY = 'lem:pwa:dismissedAt';
const LAST_SHOWN_AT_KEY = 'lem:pwa:lastShownAt';
const VISIT_COUNT_KEY = 'lem:pwa:visitCount';
const SESSION_COUNTED_KEY = 'lem:pwa:sessionCounted';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const RESHOW_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly promptVisibleSignal = signal(false);
  private readonly installedSignal = signal(this.detectStandalone());
  private readonly modeSignal = signal<PwaInstallMode>(this.detectMode());

  readonly promptVisible = this.promptVisibleSignal.asReadonly();
  readonly installed = this.installedSignal.asReadonly();
  readonly mode = this.modeSignal.asReadonly();
  readonly canOfferInstall = computed(() => !this.installedSignal() && this.modeSignal() !== 'unsupported');

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    this.registerVisit();

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.modeSignal.set('native');
      this.scheduleAutomaticSuggestion();
    });

    window.addEventListener('appinstalled', () => {
      this.installedSignal.set(true);
      this.promptVisibleSignal.set(false);
      this.deferredPrompt = null;
      this.clearReminderState();
    });

    if (this.modeSignal() === 'ios') {
      this.scheduleAutomaticSuggestion();
    }
  }

  openManually(): void {
    if (!this.canOfferInstall()) {
      return;
    }

    this.markShown();
    this.promptVisibleSignal.set(true);
  }

  close(): void {
    this.promptVisibleSignal.set(false);
  }

  remindLater(): void {
    this.storeTimestamp(DISMISSED_AT_KEY, Date.now());
    this.promptVisibleSignal.set(false);
  }

  async install(): Promise<'accepted' | 'dismissed' | 'instructions'> {
    if (this.modeSignal() === 'ios') {
      return 'instructions';
    }

    if (!this.deferredPrompt) {
      return 'dismissed';
    }

    const prompt = this.deferredPrompt;
    await prompt.prompt();
    const choice = await prompt.userChoice;

    if (choice.outcome === 'accepted') {
      this.installedSignal.set(true);
      this.clearReminderState();
    } else {
      this.storeTimestamp(DISMISSED_AT_KEY, Date.now());
    }

    this.deferredPrompt = null;
    this.promptVisibleSignal.set(false);
    return choice.outcome;
  }

  private scheduleAutomaticSuggestion(): void {
    if (!this.shouldSuggestAutomatically()) {
      return;
    }

    window.setTimeout(() => {
      if (!this.shouldSuggestAutomatically()) {
        return;
      }
      this.markShown();
      this.promptVisibleSignal.set(true);
    }, 45_000);
  }

  private shouldSuggestAutomatically(): boolean {
    if (!this.canOfferInstall()) {
      return false;
    }

    const visits = Number(localStorage.getItem(VISIT_COUNT_KEY) ?? '0');
    if (visits < 2) {
      return false;
    }

    const now = Date.now();
    const dismissedAt = this.readTimestamp(DISMISSED_AT_KEY);
    const lastShownAt = this.readTimestamp(LAST_SHOWN_AT_KEY);

    return now - dismissedAt >= DISMISS_COOLDOWN_MS && now - lastShownAt >= RESHOW_COOLDOWN_MS;
  }

  private registerVisit(): void {
    if (sessionStorage.getItem(SESSION_COUNTED_KEY) === '1') {
      return;
    }

    const visits = Number(localStorage.getItem(VISIT_COUNT_KEY) ?? '0');
    localStorage.setItem(VISIT_COUNT_KEY, String(visits + 1));
    sessionStorage.setItem(SESSION_COUNTED_KEY, '1');
  }

  private markShown(): void {
    this.storeTimestamp(LAST_SHOWN_AT_KEY, Date.now());
  }

  private readTimestamp(key: string): number {
    const value = Number(localStorage.getItem(key) ?? '0');
    return Number.isFinite(value) ? value : 0;
  }

  private storeTimestamp(key: string, value: number): void {
    localStorage.setItem(key, String(value));
  }

  private clearReminderState(): void {
    localStorage.removeItem(DISMISSED_AT_KEY);
    localStorage.removeItem(LAST_SHOWN_AT_KEY);
  }

  private detectStandalone(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
    return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
  }

  private detectMode(): PwaInstallMode {
    if (typeof window === 'undefined' || this.detectStandalone()) {
      return 'unsupported';
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(userAgent);
    return isIos ? 'ios' : 'unsupported';
  }
}
